import { Formik } from "formik";
import { useContext, useState } from "react";
import GenerateLabelFields from "../formsFields/GenerateLabelFields";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import useProfile from "../../services/utils/custom hooks/useProfile";
import validations from "../../services/validations";
import Button from "./Button";
import CustomCheckBox from "../atomic/CustomCheckBox";
import useStateAndCity from "../../services/utils/custom hooks/useStateAndCity";
import useGenerateLabel from "../../services/utils/custom hooks/Shipments/useGenerateLabel";
import * as Yup from "yup";

import GenerateLabelProps from "../../services/models/generateLabelProps.model";

export default function GenerateLabel({
  selectedRowIds,
  setLabelPopup,
}: Readonly<GenerateLabelProps>): JSX.Element {
  const ids = selectedRowIds?.map((e: any) => e?.testTypes?.id) || [];
  const containsIDFour = ids.includes(4);
  const {
    state: { selectedOrganizationData, selectedPathlabData },
  } = useContext(ApplicationState);
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isError: profileError,
  } = useProfile();
  let isPLU = profileData?.roles?.includes("PathLab User");
  let OrganisationORPathlab = isPLU
    ? selectedPathlabData
    : selectedOrganizationData;

  const date = new Date();
  const formattedDate = `${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date
    .getDate()
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { data: states } = useStateAndCity();
  const [generateLabelValues, setGenerateLabelValues] = useState({
    firstName: profileData?.user?.firstName,
    lastName: profileData?.user?.lastName,
    email: profileData?.user?.email,
    shipmentDate: formattedDate,
    state: OrganisationORPathlab?.state
      ? states?.find(
          (e: any) =>
            e?.state_code === OrganisationORPathlab?.state ||
            e?.state === OrganisationORPathlab?.state
        )?.state_code
      : "",
    stateValue: OrganisationORPathlab?.state
      ? OrganisationORPathlab?.state
      : "",
    city: OrganisationORPathlab?.city,
    zipCode: OrganisationORPathlab?.zipcode,
    phone: OrganisationORPathlab?.phone,
    address: OrganisationORPathlab?.address,
    sampleUM: false,
    orgId: OrganisationORPathlab?.id,
  });
  const { mutate: generateLabel } = useGenerateLabel(
    selectedRowIds,
    setLabelPopup,
    setSubmitting
  );

  return (
    <Formik
      initialValues={generateLabelValues}
      enableReinitialize
      validationSchema={validations.generateLabelValidation.shape({
        ...validations.generateLabelValidation.fields,
        stateValue: Yup.string().test(
          "is-valid-state",
          "State must be selected from the list",
          (value) => {
            const state = states?.find(
              (state: any) => state.state_code === value
            );

            return !!state;
          }
        ),
      })}
      validateOnMount
      initialTouched={{
        firstName: true,
        lastName: true,
        state: true,
        email: true,
        zipCode: true,
        address: true,
        city: true,
        phone: true,
        shipmentDate: true,
      }}
      onSubmit={(values: any) => {
        // generateData(values);
        generateLabel(values);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isValid,
        setFieldValue,
        ...rest
      }) => (
     <form
          onSubmit={handleSubmit}
          /* FIXED: Changed fixed w-[700px] to w-full max-w-[700px] 
             FIXED: Changed h-[630px] to max-h-[80vh] for better mobile scrolling
          */
          className="mt-4 overflow-x-hidden overflow-y-auto max-h-[75vh] md:max-h-[630px] w-full md:w-[700px] md:px-0"
        >
          <h3 className="text-royalBlue font-semibold text-sm py-3">
            Shipper Information
          </h3>
          <p className="text-royalBlue text-sm">
            Please enter shipper information below
          </p>
          <div className="flex flex-wrap overflow-auto">
            <GenerateLabelFields
              values={values}
              touched={touched}
              errors={errors}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              {...rest}
            />
          </div>
          {containsIDFour && (
            <div className="mb-2">
              <p>
                You have chosen the UM test option, please confirm if this
                shipment contains FNAB (Fine-needle aspiration biopsy) samples.
              </p>
              <div className="flex items-center">
                <CustomCheckBox
                  id="id"
                  name="sampleUM"
                  value={values.sampleUM}
                  label="Yes, the shipment contains samples of FNAB sample(s)."
                />
              </div>
            </div>
          )}
          <Button
            textInformation={{ text: "Generate", fontWeight: "font-semibold" }}
            buttonTheme="primary"
            disabled={submitting || !isValid}
            loading={submitting}
            onClick={() => onsubmit}
            className="mt-2"
          />
        </form>
      )}
    </Formik>
  );
}
